import { ChartData, ChartOptions } from 'chart.js'
import { format, parseISO } from 'date-fns'

type Metric = {
  id: string
  label: string
  unit?: 'count' | 'currency' | 'percent'
  chartType?: 'line' | 'area' | 'bar' | 'none' | 'pie'
  display?: 'tile' | 'chart' | 'both'
  series?: Array<{ x: string; y: number }>
  total?: number
}

const axisGridColor = 'rgba(0,0,0,0.06)'

export const formatDateLabel = (value: string) => {
  const parsed = parseISO(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }
  return format(parsed, 'dd/MM/yy')
}

export const formatMetricValue = (value: number, unit?: string) => {
  if (unit === 'currency') {
    return `$${Intl.NumberFormat('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)}`
  }
  if (unit === 'percent') {
    return `${Intl.NumberFormat('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)}%`
  }
  return `${value}`
}

export const formatCurrencyValue = (value: number) => {
  return `$${Intl.NumberFormat('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}`
}

export const buildChartData = (metric: Metric, color: string, area = false): ChartData<'line'> => {
  const series = metric.series ?? []
  return {
    labels: series.map(point => formatDateLabel(point.x)),
    datasets: [
      {
        label: metric.label,
        data: series.map(point => point.y),
        fill: area,
        backgroundColor: area ? `${color}33` : color,
        borderColor: color,
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 3,
      },
    ],
  }
}

export const buildBarData = (metric: Metric, color: string): ChartData<'bar'> => {
  const series = metric.series ?? []
  return {
    labels: series.map(point => formatDateLabel(point.x)),
    datasets: [
      {
        label: metric.label,
        data: series.map(point => point.y),
        backgroundColor: color,
        borderRadius: 6,
      },
    ],
  }
}

export const buildHorizontalBarData = (
  items: Array<{ label: string; value: number }>,
  color: string,
): ChartData<'bar'> => {
  return {
    labels: items.map(item => item.label),
    datasets: [
      {
        label: 'Sales',
        data: items.map(item => item.value),
        backgroundColor: color,
        borderRadius: 6,
      },
    ],
  }
}

export const baseHorizontalBarOptions: ChartOptions<'bar'> = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: 'y',
  plugins: {
    legend: { display: false },
  },
  scales: {
    x: { grid: { color: axisGridColor } },
    y: { grid: { display: false } },
  },
}

export const buildPieData = (
  items: Array<{ label: string; value: number }>,
  colors: string[],
): ChartData<'pie'> => {
  return {
    labels: items.map(item => item.label),
    datasets: [
      {
        data: items.map(item => item.value),
        backgroundColor: colors,
        borderWidth: 0,
      },
    ],
  }
}

export const basePieOptions: ChartOptions<'pie'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'right',
      labels: {
        generateLabels: chart => {
          const data = chart.data
          const labels = data.labels || []
          const values = (data.datasets?.[0]?.data || []) as number[]
          const total = values.reduce((sum, value) => sum + (Number(value) || 0), 0)
          return labels.map((label, index) => {
            const rawValue = Number(values[index] || 0)
            const percent = total > 0 ? (rawValue / total) * 100 : 0
            const text = `${label} (${percent.toFixed(2)}%)`
            return {
              text,
              fillStyle: (data.datasets?.[0]?.backgroundColor as string[])?.[index] || '#000',
              strokeStyle: '#fff',
              lineWidth: 1,
              hidden: false,
              index,
            }
          })
        },
      },
    },
    tooltip: {
      callbacks: {
        label: context => {
          const label = context.label ?? ''
          const rawValue = typeof context.raw === 'number' ? context.raw : Number(context.raw || 0)
          const formatted = rawValue.toLocaleString('en-AU', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
          return `${label}: ${formatted}%`
        },
      },
    },
  },
}

export const baseChartOptions: ChartOptions<'line' | 'bar'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
  },
  scales: {
    x: { grid: { display: false } },
    y: { grid: { color: axisGridColor } },
  },
}
